export { type AccountStatus, ACCOUNT_STATUS_LABELS, ACCOUNT_STATUS_OPTIONS, formatAccountStatus, accountStatusColor } from './accountStatus';

export type ClientContactInfo = {
  name: string | null;
  email: string | null;
  phone: string | null;
};

export type ClientSummary = {
  id: string;
  businessName: string;
  taxStatus: string | null;
  city?: string | null;
  state?: string | null;
};

export type ClientDetail = ClientSummary & {
  contacts: ClientContactInfo[];
  notes: string | null;
  metadata?: Record<string, unknown> | null;
};

export type ClientListBranch = {
  id: string;
  name: string | null;
};

export type ClientListRow = ClientSummary & {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  createdAt?: string | null;
  branches?: ClientListBranch[];
  aliasCount?: number;
  eventCount?: number;
  contactCount?: number;
  /** Posted and paid invoice subtotals, all time (after discounts, before tax). */
  revenue?: number;
  /** Messages on the customer's Emails tab; null when this viewer can't open that tab. */
  emailCount?: number | null;
};

const asString = (value: unknown): string | null => {
  if (value == null) return null;
  const str = String(value).trim();
  return str.length ? str : null;
};

const normaliseContact = (input: any): ClientContactInfo => ({
  name: asString(input?.name) ?? asString(input?.full_name) ?? null,
  email: asString(input?.email),
  phone: asString(input?.phone ?? input?.phone_number),
});

export const rowToClientSummary = (row: any): ClientSummary => ({
  id: asString(row?.client_id ?? row?.id) ?? '',
  businessName: asString(row?.business_name ?? row?.name) ?? '',
  taxStatus: asString(row?.tax_status ?? row?.taxStatus),
  city: asString(row?.city),
  state: asString(row?.state),
});

export const rowToClientDetail = (row: any): ClientDetail => {
  const summary = rowToClientSummary(row);
  const contacts = Array.isArray(row?.contacts)
    ? row.contacts.map(normaliseContact)
    : [];
  const metadata = row?.metadata && typeof row.metadata === 'object' ? row.metadata : null;
  return {
    ...summary,
    contacts,
    notes: asString(row?.notes),
    metadata,
  };
};

export const rowToClientListRow = (row: any): ClientListRow => {
  const summary = rowToClientSummary(row);
  const branches: ClientListBranch[] = Array.isArray(row?.branches)
    ? row.branches
        .map((branch: any) => ({
          id: asString(branch?.id ?? branch?.branch_id ?? branch?.client_id) ?? '',
          name: asString(branch?.branch_name ?? branch?.name),
        }))
        .filter((branch: ClientListBranch) => Boolean(branch.id))
    : [];
  const aliasCountRaw = row?.alias_count ?? row?.aliasCount;
  const aliasCount = typeof aliasCountRaw === 'number' ? aliasCountRaw : Number(aliasCountRaw ?? 0);
  const eventCountRaw = row?.event_count ?? row?.eventCount;
  const eventCount = typeof eventCountRaw === 'number' ? eventCountRaw : Number(eventCountRaw ?? 0);
  const contactCountRaw = row?.contact_count ?? row?.contactCount;
  const contactCount = typeof contactCountRaw === 'number' ? contactCountRaw : Number(contactCountRaw ?? 0);
  const revenueRaw = Number(row?.revenue ?? 0);
  const emailCountRaw = row?.email_count ?? row?.emailCount;
  const emailCount = emailCountRaw == null ? null : Number(emailCountRaw);
  return {
    ...summary,
    addressLine1: asString(row?.address_line1),
    addressLine2: asString(row?.address_line2),
    city: asString(row?.city),
    state: asString(row?.state),
    createdAt: asString(row?.created_at ?? row?.createdAt),
    branches,
    aliasCount,
    eventCount,
    contactCount,
    revenue: Number.isFinite(revenueRaw) ? revenueRaw : 0,
    emailCount: emailCount != null && Number.isFinite(emailCount) ? emailCount : null,
  };
};

export const clientDetailToUpsertInput = (detail: ClientDetail): Record<string, unknown> => ({
  id: detail.id || undefined,
  business_name: detail.businessName.trim(),
  tax_status: detail.taxStatus ?? null,
  contacts: detail.contacts.map(contact => ({
    name: contact.name,
    email: contact.email,
    phone: contact.phone,
  })),
  notes: detail.notes ?? null,
  metadata: detail.metadata ?? {},
});

const emailPattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const validateClientContactInfo = (contact: ClientContactInfo): string | null => {
  if (contact.email && !emailPattern.test(contact.email)) {
    return 'Provide a valid email address.';
  }
  if (contact.phone && contact.phone.replace(/\D/g, '').length < 7) {
    return 'Provide a valid phone number.';
  }
  return null;
};
